import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { FolderOpen, MapPin, Banknote, Users, Star } from "lucide-react";
import ReviewModal from "@/components/ReviewModal";
import SafeAvatar from "@/components/SafeAvatar";

const URG_LABEL = { urgent: "Urgent", "1-semaine": "Sous 1 semaine", "1-mois": "Sous 1 mois", flexible: "Flexible" };

export default function PartRequests() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeInvite, setActiveInvite] = useState(null);

  const reload = async () => {
    const [l, inv] = await Promise.all([
      api.get("/leads/mine"),
      api.get("/reviews/pending-invitations").catch(() => ({ data: [] })),
    ]);
    setItems(l.data);
    setInvites(inv.data || []);
    setLoading(false);
  };

  useEffect(() => { reload(); }, []);

  return (
    <AppShell role="particulier" title="Mes demandes">
      {loading && <div className="text-center py-10 text-slate2">Chargement…</div>}

      {!loading && invites.length > 0 && (
        <section className="mb-4">
          <h3 className="text-xs uppercase tracking-widest text-slate2 font-semibold mb-2 flex items-center gap-1.5">
            <Star className="w-3.5 h-3.5 fill-brand text-brand" /> Avis à laisser ({invites.length})
          </h3>
          <div className="space-y-2">
            {invites.map((inv) => (
              <button
                key={`${inv.lead_id}-${inv.pro?.id}`}
                onClick={() => setActiveInvite(inv)}
                className="w-full bg-white rounded-2xl border border-border p-3 flex items-center gap-3 text-left tap-scale hover:border-brand"
                data-testid={`review-invite-${inv.pro?.id}`}
              >
                <SafeAvatar src={inv.pro?.avatar} role="pro" name={inv.pro?.name} className="w-11 h-11 rounded-xl object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold text-sm text-ink truncate">{inv.pro?.name}</div>
                  <div className="text-[11px] text-slate2 truncate">Chantier : {inv.lead_title}</div>
                </div>
                <span className="text-[10px] font-bold uppercase tracking-widest text-brand bg-brand/10 rounded-full px-2 py-1">
                  Noter
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      {!loading && items.length === 0 && (
        <div className="bg-white rounded-2xl border border-border p-8 text-center">
          <div className="h-14 w-14 rounded-2xl bg-bone mx-auto flex items-center justify-center mb-3">
            <FolderOpen className="w-6 h-6 text-slate2" />
          </div>
          <h3 className="font-heading font-bold text-ink">Aucune demande</h3>
          <p className="text-sm text-slate2 mt-1.5">Créez votre première demande pour recevoir des devis.</p>
          <button onClick={() => nav("/p/create")} className="mt-4 bg-brand text-white rounded-full px-5 py-2 text-sm font-semibold tap-scale" data-testid="empty-create-btn">Créer une demande</button>
        </div>
      )}
      <div className="space-y-3">
        {items.map((L) => (
          <article key={L.id} className="bg-white rounded-2xl border border-border p-4" data-testid={`my-lead-${L.id}`}>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-brand/10 text-brand">{URG_LABEL[L.urgency]}</span>
              <span className="text-[10px] uppercase tracking-wider text-slate2">{new Date(L.created_at).toLocaleDateString("fr-FR")}</span>
            </div>
            <h3 className="font-heading font-bold text-ink">{L.title}</h3>
            <p className="text-sm text-slate2 mt-1 line-clamp-2">{L.description}</p>
            <div className="flex items-center gap-3 text-xs text-slate2 mt-2">
              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {L.city}</span>
              <span className="flex items-center gap-1"><Banknote className="w-3 h-3" /> {L.budget_min}-{L.budget_max}€</span>
              <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {(L.unlocks || []).length}/3 pros</span>
            </div>
          </article>
        ))}
      </div>

      <ReviewModal
        open={!!activeInvite}
        onOpenChange={(v) => { if (!v) setActiveInvite(null); }}
        invite={activeInvite}
        onSubmitted={reload}
      />
    </AppShell>
  );
}
